import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Network, DoorOpen, ClipboardList, ArrowRightFromLine, ChevronDown, ChevronRight, LogOut, X, Receipt, FileSpreadsheet, BadgeDollarSign, BarChart3, FileBarChart, UsersIcon, ReceiptIcon, LayoutDashboardIcon, TicketIcon, WalletIcon, CpuIcon, MonitorIcon } from 'lucide-react';
export type PageId = 'gate-entry' | 'vehicle-entry-detail' | 'vehicle-final-exit' | 'create-billing' | 'billed-details' | 'billing-and-exit' | 'vehicle-detention-report' | 'user-report' | 'mis-report' | 'pos-vehicle-detention' | 'pos-user-report' | 'coupon-generation' | 'coupon-used-report' | 'pos-coupon-used-report' | 'hardware-status';
interface SidebarProps {
  activePage: PageId;
  onPageChange: (page: PageId) => void;
  userName: string;
  onLogout: () => void;
  isOpen: boolean;
  onClose: () => void;
}
interface NavSection {
  id: string;
  label: string;
  icon: React.ElementType;
  accentColor: string;
  dotColor: string;
  items: {
    id: PageId;
    label: string;
    icon: React.ElementType;
  }[];
}
export function Sidebar({
  activePage,
  onPageChange,
  userName,
  onLogout,
  isOpen,
  onClose
}: SidebarProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    gateOps: true,
    billing: true,
    reports: true,
    posReports: true,
    finance: true,
    hardware: true
  });
  const toggleSection = (key: string) => {
    setOpenSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const sections: NavSection[] = [{
    id: 'gateOps',
    label: 'Gate Operations',
    icon: DoorOpen,
    accentColor: 'text-blue-400',
    dotColor: 'bg-blue-400',
    items: [{
      id: 'gate-entry',
      label: 'Gate Entry',
      icon: DoorOpen
    }, {
      id: 'vehicle-entry-detail',
      label: 'Vehicle Entry Detail',
      icon: ClipboardList
    }, {
      id: 'vehicle-final-exit',
      label: 'Vehicle Final Exit',
      icon: ArrowRightFromLine
    }]
  }, {
    id: 'billing',
    label: 'Billing',
    icon: Receipt,
    accentColor: 'text-amber-400',
    dotColor: 'bg-amber-400',
    items: [{
      id: 'create-billing',
      label: 'Create Billing',
      icon: Receipt
    }, {
      id: 'billed-details',
      label: 'Billed Details',
      icon: FileSpreadsheet
    }, {
      id: 'billing-and-exit',
      label: 'Billing & Exit',
      icon: BadgeDollarSign
    }]
  }, {
    id: 'reports',
    label: 'Reports (A4)',
    icon: BarChart3,
    accentColor: 'text-violet-400',
    dotColor: 'bg-violet-400',
    items: [{
      id: 'vehicle-detention-report',
      label: 'Vehicle Detention',
      icon: FileBarChart
    }, {
      id: 'user-report',
      label: 'User Report',
      icon: UsersIcon
    }, {
      id: 'mis-report',
      label: 'MIS Report',
      icon: LayoutDashboardIcon
    }]
  }, {
    id: 'posReports',
    label: 'POS Reports (80mm)',
    icon: ReceiptIcon,
    accentColor: 'text-emerald-400',
    dotColor: 'bg-emerald-400',
    items: [{
      id: 'pos-vehicle-detention',
      label: 'Vehicle Detention',
      icon: FileBarChart
    }, {
      id: 'pos-user-report',
      label: 'User Report',
      icon: UsersIcon
    }, {
      id: 'pos-coupon-used-report',
      label: 'Coupon Used',
      icon: TicketIcon
    }]
  }, {
    id: 'finance',
    label: 'Finance',
    icon: WalletIcon,
    accentColor: 'text-orange-400',
    dotColor: 'bg-orange-400',
    items: [{
      id: 'coupon-generation',
      label: 'Coupon Generation',
      icon: TicketIcon
    }, {
      id: 'coupon-used-report',
      label: 'Coupon Used Report',
      icon: FileSpreadsheet
    }]
  }, {
    id: 'hardware',
    label: 'Hardware',
    icon: CpuIcon,
    accentColor: 'text-cyan-400',
    dotColor: 'bg-cyan-400',
    items: [{
      id: 'hardware-status',
      label: 'Hardware Status',
      icon: MonitorIcon
    }]
  }];
  const SidebarContent = <div className="flex flex-col h-full bg-brand-navy text-slate-300 w-64 shrink-0" data-id="element-184">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-800" data-id="element-185">
        <div className="flex items-center gap-3" data-id="element-186">
          <div className="bg-brand-teal/10 p-1.5 rounded-lg border border-brand-teal/20" data-id="element-187">
            <Network className="w-5 h-5 text-brand-teal" data-id="element-188" />
          </div>
          <div data-id="element-189">
            <span className="text-lg font-bold tracking-tight text-white block leading-tight" data-id="element-190">
              ERPLogiPark
            </span>
          </div>
        </div>
        <button onClick={onClose} className="lg:hidden p-2 -mr-2 text-slate-400 hover:text-white rounded-lg min-h-[48px] min-w-[48px] flex items-center justify-center" data-id="element-191">
          <X className="w-5 h-5" data-id="element-192" />
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-3" data-id="element-193">
        {sections.map((section, sectionIdx) => {
        const SectionIcon = section.icon;
        const isOpen = openSections[section.id] ?? true;
        const hasActiveChild = section.items.some(item => activePage === item.id);
        return <div key={section.id} data-id="element-194">
              {/* Section divider */}
              {sectionIdx > 0 && <div className="mx-5 my-1.5 border-t border-slate-800/60" data-id="element-195" />}

              <div className="px-3 mb-1" data-id="element-196">
                {/* Section Header */}
                <button onClick={() => toggleSection(section.id)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all min-h-[40px] group ${hasActiveChild && !isOpen ? 'bg-slate-800/40' : 'hover:bg-slate-800/30'}`} data-id="element-197">
                  <div className="flex items-center gap-2.5" data-id="element-198">
                    <div className={`w-1.5 h-1.5 rounded-full ${section.dotColor} shrink-0`} data-id="element-199" />
                    <SectionIcon className={`w-4 h-4 ${section.accentColor} shrink-0`} data-id="element-200" />
                    <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider group-hover:text-slate-100 transition-colors" data-id="element-201">
                      {section.label}
                    </span>
                  </div>
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" data-id="element-202" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" data-id="element-203" />}
                </button>

                {/* Section Items */}
                <AnimatePresence initial={false} data-id="element-204">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-205">
                      <div className="space-y-0.5 mt-1 ml-3 border-l border-slate-800/60 pl-0" data-id="element-206">
                        {section.items.map(item => {
                    const isActive = activePage === item.id;
                    const Icon = item.icon;
                    return <button key={item.id} onClick={() => {
                      onPageChange(item.id);
                      onClose();
                    }} className={`w-full flex items-center gap-2.5 pl-4 pr-3 py-2.5 rounded-r-lg transition-all min-h-[40px] ${isActive ? 'bg-brand-teal/10 text-brand-teal font-medium border-l-2 border-brand-teal -ml-[1px]' : 'text-slate-400 hover:bg-slate-800/40 hover:text-slate-200'}`} data-id="element-207">
                              <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-brand-teal' : 'text-slate-500'}`} data-id="element-208" />
                              <span className="text-[13px]" data-id="element-209">{item.label}</span>
                            </button>;
                  })}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>;
      })}
      </div>

      {/* Footer / User Info */}
      <div className="p-4 border-t border-slate-800" data-id="element-210">
        <div className="flex items-center justify-between px-2 py-2" data-id="element-211">
          <div className="flex flex-col truncate pr-2" data-id="element-212">
            <span className="text-xs text-slate-500" data-id="element-213">Logged in as</span>
            <span className="text-sm font-medium text-slate-300 truncate" data-id="element-214">
              {userName}
            </span>
          </div>
          <button onClick={onLogout} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center" title="Logout" data-id="element-215">
            <LogOut className="w-5 h-5" data-id="element-216" />
          </button>
        </div>
      </div>
    </div>;
  return <>
      {/* Desktop Sidebar (Fixed) */}
      <div className="hidden lg:block h-screen sticky top-0" data-id="element-217">
        {SidebarContent}
      </div>

      {/* Mobile Sidebar (Overlay) */}
      <AnimatePresence data-id="element-218">
        {isOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} onClick={onClose} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden" data-id="element-219" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          bounce: 0,
          duration: 0.3
        }} className="fixed inset-y-0 left-0 z-50 lg:hidden shadow-2xl" data-id="element-220">
              {SidebarContent}
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}